import AdminCategoryCard from '@components/AdminCategoryCard/AdminCategoryCard.component'
import AdminPageHeader from '@components/AdminPageHeader/AdminPageHeader.component'
import { Box, Button, CircularProgress, FormLabel, IconButton, InputAdornment, MenuItem, styled, TextareaAutosize, TextField, Typography} from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Route as AdminDashboardRoute } from './_admin.admin-dashboard';
import { useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReusableModal from '@components/Modal/ReusableModal.component';
import SearchIcon from 'icons/Search.icon';
import AddIcon from '@mui/icons-material/Add';
import NoData from 'icons/NoData.icon';

export const Route = createFileRoute('/(admin)/_admin/create-technology')({
  component: RouteComponent,
})

const StyledTextArea = styled(TextareaAutosize)({
  backgroundColor: '#F9F9F9',
  width: '100%',
  minHeight: '135px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid var(--Interviewer-Lightest-Black)',
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '20px',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
  },
})

const StyledSearch = styled(TextField)(() => ({
  marginTop: '40px',
  input: {
    lineHeight: '20px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Interviewer-Lightest-Black)',
  },
  '@media (max-width: 768px)': {
    marginTop: '16px',
  }
}));

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [field, setField] = useState('');
  const [description, setDescription] = useState('');
  const [isCreated, setIsCreated] = useState(false);
  const [idOfNewTechnology, setIdOfNewTechnology] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');


  // Kreiranje tehnologije
  const createCategoryMutation = useMutation({
    mutationFn: async() => {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://interviewer.runasp.net/api/technologies', {
        name: name,
        description: description, 
        field: field
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      return response;
    },
    onSuccess: (data) => {
      setIdOfNewTechnology(data.data);
      toast.success('Technology was saccessfully created!')
      setIsCreated(true);
    },
    onError: (error) => {
      console.log(error);
    }
  })

  //Podaci za kategorije koje su linkovane sa tehnologijim
  const { data: linkedCategories, isLoading: isLoadingLinkedCategory,  } = useQuery({
    queryKey: ['technology-categories-techpage', idOfNewTechnology],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/technologies/${idOfNewTechnology}/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('Linkovane kategorije');
      return response.data;
    },
    enabled: !!idOfNewTechnology,
  });

  // Id svih linkovanih kategorija
  const linkedCategoryIds = linkedCategories?.map(cat => cat.id) || [];

  //Linkovanje kategorije za postojecu tehnologiju
  const linkCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://interviewer.runasp.net/api/technologies/${idOfNewTechnology}/link-category`,
        { categoryId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['technology-categories-techpage', idOfNewTechnology] });
      toast.success('Successfully linked category!')
    },
    onError: (error) => {
      toast.error('We have problem to link category!')
      console.log(error);
    }
  })

  // Podaci za postojece (globalne) kategorije - modal
  const { data: existCategories } = useQuery({
    queryKey: ['existing-categories-techpage'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const results = await axios.get(`https://interviewer.runasp.net/api/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return results.data;
    },
    enabled: isModalOpen,
  });

  //Brisanje linkovane kategorije - unlink
  const handleDeleteCategory = (categoryId: string) => {
     deleteCategoryMutation.mutate(categoryId);
  }
  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      const token = localStorage.getItem("token");
      return axios.delete(
        `https://interviewer.runasp.net/api/categories/${categoryId}/technologies/${idOfNewTechnology}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['technology-categories-techpage', idOfNewTechnology]})
      toast.success('Successfully unlinked category!');
    },
    onError: (err) => {
      console.log("Delete error:", err);
    },
  })

  return (
    <Box>
      <AdminPageHeader
        title="Create a technology"
        icon={<ArrowBackIcon color="primary" fontSize="medium" />} 
        onIconClick={() => navigate({ to: AdminDashboardRoute.to })}
      />
        <Box sx={{
          px: {
            xs: '16px',
            md: '32px'    
          },
          py: {
            xs: '16px',
            md: '32px'
          }
        }}>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: {xs: '20px', md: '48px'}}}>
              <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: '20px'}}>
                <TextField 
                      label="Technology Name"
                      variant="outlined"
                      fullWidth
                      value={name}
                      onChange={e => setName(e.target.value)}
                />
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px' ,width: '100%'}}>
                      <TextField 
                          label="Technology Field"
                          variant="outlined"
                          fullWidth
                          select
                          value={field}
                          onChange={e => setField(e.target.value)}
                      >
                          <MenuItem value={1}>Mobile</MenuItem>
                          <MenuItem value={2}>Desktop</MenuItem>
                          <MenuItem value={3}>Web</MenuItem>
                      </TextField>
                      {/* <Typography sx={{color: 'var(--Interviewer-Green)'}}>Add a new technology field</Typography> */}
                </Box>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                <FormLabel sx={{fontSize: '16px', fontWeight: '500', color: 'var(--Interviewer-Black)'}}>Short Technology Description</FormLabel> 
                <StyledTextArea 
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
              </Box>
              {!isCreated && (
                <Button variant="contained" sx={{display: 'flex', marginLeft: 'auto'}} onClick={() => createCategoryMutation.mutate()}>Create Technology</Button>
              )}
          </Box>
          {isCreated && (
            <>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '48px', mb: '16px'}}>
                  <Typography sx={{fontSize: '16px', fontWeight: '500', color: '--var(--Interviewer-Black)'}}>Categories</Typography>
                  <Box sx={{display: 'flex', gap: '12px'}}>
                      <Button 
                        variant='outlined' 
                        sx={{
                          backgroundColor: 'var(--Light-Gray)',
                          border: 'none',
                          color: 'var(--Interviewer-Black)',
                          fontWeight: 600,
                          fontSize: '14px',
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        // onClick={() => navigate({ 
                        //   to: '/create-category', 
                        //   search: { technologyId: id, categoryId: undefined } 
                        // })}
                      >
                        Create a new category
                      </Button>
                      <Button variant="contained" onClick={() => setModalOpen(true)}>Link existing category</Button>
                  </Box>
              </Box>
              <Box sx={{
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px'
              }}>
                  {isLoadingLinkedCategory && <CircularProgress />}
                  {linkedCategories && linkedCategories.length === 0 && <NoData />}
                  {linkedCategories && linkedCategories.map((category: any) => (
                    <AdminCategoryCard 
                      key={category.id} 
                      id={category.id}
                      name={category.name} 
                      description={category.description} 
                      // onEdit={(categoryId) => navigate({ to: '/create-category', search: { technologyId: id, categoryId } })}
                      onDelete={handleDeleteCategory}
                    />
                  ))}
              </Box>
            </>
          )}

          {/* Modal */}
          <ReusableModal open={isModalOpen} onClose={() => setModalOpen(false)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="h2" sx={{fontSize: '24px'}}>
                Link existing category
              </Typography>
              <Typography sx={{fontSize: '14px'}}>
                Link a category from the list or search for a specific category
              </Typography>
            </Box>
            <StyledSearch
              fullWidth
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              placeholder="Search for a category"
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                mt: '20px',
                width: '100%',
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              {existCategories?.map(category => {
                  const isLinked = linkedCategoryIds.includes(category.id);
                  return (
                    <Box key={category.id} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: { xs: 1.5, sm: 2 },
                      border: '1px solid var(--Light-Gray)',
                      borderRadius: '8px',
                      gap: {xs: '4px', md: '0px'},
                      opacity: isLinked ? 0.5 : 1,
                    }}>
                      <Box>
                        <Typography variant="h3" sx={{pb: '8px'}}>
                          {category.name}
                        </Typography>
                        <Typography sx={{fontSize: '14px'}}>
                          {category.description}
                        </Typography>
                      </Box>
                      <IconButton
                        color="primary"
                        onClick={() => linkCategoryMutation.mutate(category.id)}
                        disabled={isLinked}
                        sx={{
                          backgroundColor: 'black',
                          color: 'white',
                          borderRadius: '50%',
                          p: '8px',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'var(--Light-Gray)',
                          },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  );
                })
                }
            </Box>
          </ReusableModal>
        </Box>
    </Box>
  )
}
